import { call, put, select, takeEvery } from "redux-saga/effects";
import moment from "moment-timezone";
import { getDataFromSxi, getDataFromRDS, getJwtToken } from "api";
import { ENDPOINT, PICTREE } from "../../constants";
import {
  setPageTopLoader,
  setBudgetSummaryDataTepco,
  setBudgetSummaryDataNtt,
  setBudgetSummaryDataNttFt,
  setBudgetSummaryDataHokuden,
  setBudgetRemainTrendDataTepco,
  setBudgetRemainTrendDataNtt,
  setBudgetRemainTrendDataNttFt,
  setBudgetRemainTrendDataHokuden,
  setBudgetSourceLoadingTepco,
  setBudgetSourceLoadingNtt,
  setBudgetSourceLoadingNttFt,
  setBudgetSourceLoadingHokuden,
  setBudgetBoostSourceLoadingTepco,
  setBudgetBoostSourceLoadingNtt,
  setBudgetBoostSourceLoadingNttFt,
  setBudgetBoostSourceLoadingHokuden,
  setBudgetBoostDataTepco,
  setBudgetBoostDataNtt,
  setBudgetBoostDataNttFt,
  setBudgetBoostDataHokuden,
  setBudgetBoostLoadingTepco,
  setBudgetBoostLoadingNtt,
  setBudgetBoostLoadingNttFt,
  setBudgetBoostLoadingHokuden,
  setBudgetBoostSourceDataTepco,
  setBudgetBoostSourceDataNtt,
  setBudgetBoostSourceDataNttFt,
  setBudgetBoostSourceDataHokuden,
} from "actions";

const REGULAR_SEASON_KEYWORDS = ["レギュラーシーズン", "regular season"];

const BUDGET_TREND_CAPITAL_BUDGET_IDS = {
  tepco: [
    "cb_mig_cmn8t73ia0001jmpr4eig3uah",
    "cb_mig_cmn8twz7r0001jlpr7yotbqg3",
    "cb_mig_cmn8tzajq0001xhpr5huq9xdp",
  ],
  ntt: ["capital_budget_cmp4yz2lj00017rtcc4ok0jgv"],
  "ntt-ft": [],
  hokuden: [],
};

const BUDGET_TREND_METADATA = {
  tepco: {
    "cb_mig_cmn8t73ia0001jmpr4eig3uah": {
      code: "CHIBA1",
      name: "ツルツタ千葉1",
      color: "#3F7ECC",
    },
    "cb_mig_cmn8twz7r0001jlpr7yotbqg3": {
      code: "IBARAKI1",
      name: "ツルツタ茨城1",
      color: "#E4961A",
    },
    "cb_mig_cmn8tzajq0001xhpr5huq9xdp": {
      code: "SAITAMA1",
      name: "ツルツタ埼玉1",
      color: "#D45F2D",
    },
  },
  ntt: {
    "capital_budget_cmp4yz2lj00017rtcc4ok0jgv": {
      code: "RS",
      name: "レギュラーS",
      color: "#3F7ECC",
    },
  },
};

const FALLBACK_COLORS = ["#3F7ECC", "#E4961A", "#D45F2D", "#5E8BFF", "#77B255", "#A95AA1"];
const BUDGET_COIN_TO_YEN_RATE = 12;
const BUDGET_REMAIN_TREND_RDS_SEASON_ID = "cmp4yz2lj00017rtcc4ok0jgv";
const BUDGET_REMAIN_TREND_RDS_TABLE = "PicTreeKpiRegularSeasonBudgetRemainTrendAggProd";
const BUDGET_REMAIN_TREND_RDS_COMPANY_CODES = {
  ntt: "NTT_ME",
  tepco: "TEPCO",
};

const getBudgetSummarySetter = (companyCode) => {
  switch (companyCode) {
    case "ntt":
      return setBudgetSummaryDataNtt;
    case "ntt-ft":
      return setBudgetSummaryDataNttFt;
    case "hokuden":
      return setBudgetSummaryDataHokuden;
    case "tepco":
    default:
      return setBudgetSummaryDataTepco;
  }
};

const getBudgetTrendSetter = (companyCode) => {
  switch (companyCode) {
    case "ntt":
      return setBudgetRemainTrendDataNtt;
    case "ntt-ft":
      return setBudgetRemainTrendDataNttFt;
    case "hokuden":
      return setBudgetRemainTrendDataHokuden;
    case "tepco":
    default:
      return setBudgetRemainTrendDataTepco;
  }
};

const getBudgetSourceLoadingSetter = (companyCode) => {
  switch (companyCode) {
    case "ntt":
      return setBudgetSourceLoadingNtt;
    case "ntt-ft":
      return setBudgetSourceLoadingNttFt;
    case "hokuden":
      return setBudgetSourceLoadingHokuden;
    case "tepco":
    default:
      return setBudgetSourceLoadingTepco;
  }
};

const getBudgetBoostSourceLoadingSetter = (companyCode) => {
  switch (companyCode) {
    case "ntt":
      return setBudgetBoostSourceLoadingNtt;
    case "ntt-ft":
      return setBudgetBoostSourceLoadingNttFt;
    case "hokuden":
      return setBudgetBoostSourceLoadingHokuden;
    case "tepco":
    default:
      return setBudgetBoostSourceLoadingTepco;
  }
};

const getBudgetBoostLoadingSetter = (companyCode) => {
  switch (companyCode) {
    case "ntt":
      return setBudgetBoostLoadingNtt;
    case "ntt-ft":
      return setBudgetBoostLoadingNttFt;
    case "hokuden":
      return setBudgetBoostLoadingHokuden;
    case "tepco":
    default:
      return setBudgetBoostLoadingTepco;
  }
};

const getBudgetBoostSetter = (companyCode) => {
  switch (companyCode) {
    case "ntt":
      return setBudgetBoostDataNtt;
    case "ntt-ft":
      return setBudgetBoostDataNttFt;
    case "hokuden":
      return setBudgetBoostDataHokuden;
    case "tepco":
    default:
      return setBudgetBoostDataTepco;
  }
};

const getBudgetBoostSourceSetter = (companyCode) => {
  switch (companyCode) {
    case "ntt":
      return setBudgetBoostSourceDataNtt;
    case "ntt-ft":
      return setBudgetBoostSourceDataNttFt;
    case "hokuden":
      return setBudgetBoostSourceDataHokuden;
    case "tepco":
    default:
      return setBudgetBoostSourceDataTepco;
  }
};

const getBudgetSelectors = (companyCode) => {
  switch (companyCode) {
    case "ntt":
      return {
        selectedSeason: (state) => state.pictreeNtt.selectedSeason,
        dateRange: (state) => state.page.dateRangePicTreeNtt,
      };
    case "ntt-ft":
      return {
        selectedSeason: (state) => state.pictreeNttFt.selectedSeason,
        dateRange: (state) => state.page.dateRangePicTreeNttFt,
      };
    case "hokuden":
      return {
        selectedSeason: (state) => state.pictreeHokuden.selectedSeason,
        dateRange: (state) => state.page.dateRangePicTreeHokuden,
      };
    case "tepco":
    default:
      return {
        selectedSeason: (state) => state.pictree.selectedSeason,
        dateRange: (state) => state.page.dateRangePicTree,
      };
  }
};

const BUDGET_ASSET_JOIN_CONFIG = {
  tepco: {
    detailTable: "power_asset_detail_of_tepco",
    detailAlias: "pad",
  },
  ntt: {
    detailTable: "power_asset_detail_of_ntt_me",
    detailAlias: "pad",
  },
  "ntt-ft": {
    detailTable: "power_asset_detail_of_ntt_ft",
    detailAlias: "pad",
  },
  hokuden: {
    detailTable: "power_asset_detail_of_hepco",
    detailAlias: "pad",
  },
};

const BUDGET_QUERY_CONFIG = {
  tepco: {
    multiCompany: false,
  },
  ntt: {
    multiCompany: false,
  },
  "ntt-ft": {
    multiCompany: false,
  },
  hokuden: {
    multiCompany: false,
  },
};

const buildBudgetAssetJoinClause = (companyCode) => {
  const config = BUDGET_ASSET_JOIN_CONFIG[companyCode];

  const baseJoin = `
      JOIN power_asset pa
        ON pa.id = gspab.power_asset_id
  `;

  if (!config) {
    return baseJoin;
  }

  return `${baseJoin}
      JOIN ${config.detailTable} ${config.detailAlias}
        ON ${config.detailAlias}.power_asset_id = pa.id`;
};

const buildBudgetBaseJoinClause = (companyCode) => {
  const assetJoinClause = shouldJoinBudgetAssetDetail(companyCode) ? buildBudgetAssetJoinClause(companyCode) : "";

  return `
      FROM budget pab
      JOIN game_space_power_asset_budget_budget_bind gspabb
        ON gspabb.budget_id = pab.id
      JOIN game_space_power_asset_budget gspab
        ON gspab.id = gspabb.asset_budget_id
      ${assetJoinClause}
  `;
};

const shouldJoinBudgetAssetDetail = (companyCode) => {
  const config = BUDGET_QUERY_CONFIG[companyCode];
  return config ? config.multiCompany !== false : true;
};

const isRegularSeason = (season) => {
  const seasonName = String(season?.seasonName || "");
  return REGULAR_SEASON_KEYWORDS.some((keyword) => seasonName.includes(keyword));
};

const buildGameSpaceCondition = (selectedSeasonData) => {
  const seasons = Array.isArray(selectedSeasonData) ? selectedSeasonData : [];
  const selectedSeasonIds = seasons
    .map((season) => season && season.seasonId)
    .filter(Boolean);
  const hasRegularSeason = seasons.some(isRegularSeason);
  const nonRegularSeasonIds = selectedSeasonIds.filter((id) => {
    const matched = seasons.find((season) => season && season.seasonId === id);
    return !isRegularSeason(matched);
  });

  const clauses = [];
  if (hasRegularSeason) {
    clauses.push("gspab.game_space_id IS NULL");
  }
  if (nonRegularSeasonIds.length > 0) {
    const idList = nonRegularSeasonIds.map((id) => `'${id}'`).join(", ");
    clauses.push(`gspab.game_space_id IN (${idList})`);
  }

  if (clauses.length === 0) {
    return null;
  }

  return clauses.length === 1
    ? `AND ${clauses[0]}`
    : `AND (${clauses.join(" OR ")})`;
};

const buildGameSpaceIdCondition = (selectedSeasonData, alias) => {
  const seasons = Array.isArray(selectedSeasonData) ? selectedSeasonData : [];
  const selectedSeasonIds = seasons
    .map((season) => season && season.seasonId)
    .filter(Boolean);

  if (selectedSeasonIds.length === 0) {
    return null;
  }

  const idList = selectedSeasonIds.map((id) => `'${id}'`).join(", ");
  return `AND ${alias}.game_space_id IN (${idList})`;
};

const buildUtcRange = (dateRange) => {
  if (!Array.isArray(dateRange) || !dateRange[0] || !dateRange[1]) {
    return null;
  }

  return {
    startUtc: moment
      .tz(`${dateRange[0]} 00:00:00`, "Asia/Tokyo")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss"),
    endUtc: moment
      .tz(`${dateRange[1]} 00:00:00`, "Asia/Tokyo")
      .add(1, "day")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss"),
  };
};

const getBudgetTrendIds = (companyCode) => BUDGET_TREND_CAPITAL_BUDGET_IDS[companyCode] || [];

const getBudgetTrendMeta = (companyCode, capitalBudgetId, index) => {
  const companyMeta = BUDGET_TREND_METADATA[companyCode] || {};
  const fallbackCode = capitalBudgetId ? String(capitalBudgetId).slice(-6).toUpperCase() : `FUND${index + 1}`;
  const fallbackColor = FALLBACK_COLORS[index % FALLBACK_COLORS.length];

  return (
    companyMeta[capitalBudgetId] || {
      code: fallbackCode,
      name: capitalBudgetId || `原資${index + 1}`,
      color: fallbackColor,
    }
  );
};

const toMillionValue = (amount) => Number((Number(amount || 0) / 1000000).toFixed(1));

const toYenAmount = (amount) => Number(amount || 0) / BUDGET_COIN_TO_YEN_RATE;

const formatBudgetAmount = (amount) => {
  const numericAmount = Number(amount || 0);
  if (!Number.isFinite(numericAmount)) {
    return "¥--";
  }

  if (Math.abs(numericAmount) < 10000000) {
    return `¥${numericAmount.toLocaleString("ja-JP")}`;
  }

  return `¥${(numericAmount / 1000000).toFixed(1)}M`;
};

const extractSingleRowValue = (rows, key) => {
  const row = Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
  return Number(row?.[key] || 0);
};

const getRangeDates = (dateRange) => {
  if (!Array.isArray(dateRange) || !dateRange[0] || !dateRange[1]) {
    return [];
  }

  const start = moment.tz(dateRange[0], "Asia/Tokyo").startOf("day");
  const end = moment.tz(dateRange[1], "Asia/Tokyo").startOf("day");

  const dates = [];
  for (let cursor = start.clone(); cursor.isSameOrBefore(end, "day"); cursor.add(1, "day")) {
    dates.push(cursor.format("YYYY-MM-DD"));
  }

  return dates;
};

const buildBudgetSummaryTotalQuery = (companyCode, selectedSeasonData) => {
  const seasonCondition = buildGameSpaceCondition(selectedSeasonData);

  if (!seasonCondition) {
    return null;
  }

  return {
    query: `
      SELECT COALESCE(SUM(pab.initial_balance_amount), 0) AS total_budget_amount
      ${buildBudgetBaseJoinClause(companyCode)}
      WHERE ${seasonCondition.replace(/^AND\s+/, "")}
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const buildBudgetSummaryConsumedQuery = (companyCode, selectedSeasonData, dateRange) => {
  const seasonCondition = buildGameSpaceCondition(selectedSeasonData);
  const utcRange = buildUtcRange(dateRange);

  if (!seasonCondition || !utcRange) {
    return null;
  }

  return {
    query: `
      SELECT
        COALESCE(SUM(pabeorg.reward_amount), 0) AS consumed_budget_amount
      ${buildBudgetBaseJoinClause(companyCode)}
      JOIN budget_event pabe
        ON pabe.budget_id = pab.id
      JOIN budget_event_of_reward_granted pabeorg
        ON pabeorg.id = pabe.id
      WHERE ${seasonCondition.replace(/^AND\s+/, "")}
        AND pabe.occurred_datetime >= '${utcRange.startUtc}'
        AND pabe.occurred_datetime < '${utcRange.endUtc}'
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const buildBudgetSummaryRemainingQuery = (companyCode, selectedSeasonData) => {
  const seasonCondition = buildGameSpaceCondition(selectedSeasonData);

  if (!seasonCondition) {
    return null;
  }

  return {
    query: `
      SELECT
        COALESCE(SUM(pab.balance_amount), 0) AS remaining_budget_amount
      ${buildBudgetBaseJoinClause(companyCode)}
      WHERE ${seasonCondition.replace(/^AND\s+/, "")}
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const getBudgetSummarySourceMeta = (companyCode) => {
  if (companyCode === "ntt") {
    return { code: "RS", name: "レギュラーS" };
  }
  if (companyCode === "ntt-ft") {
    return { code: "NTT-FT", name: "NTT FT" };
  }
  if (companyCode === "hokuden") {
    return { code: "HOKUDEN", name: "HOKUDEN" };
  }
  return { code: "TEPCO", name: "TEPCO" };
};

const buildBudgetSummarySourceRows = (companyCode, totalBudgetAmount, consumedBudgetAmount, remainingBudgetAmount) => {
  const meta = getBudgetSummarySourceMeta(companyCode);
  const progress = totalBudgetAmount > 0
    ? Number(((consumedBudgetAmount / totalBudgetAmount) * 100).toFixed(1))
    : 0;

  return [{
    code: meta.code,
    name: meta.name,
    budget: formatBudgetAmount(totalBudgetAmount),
    used: formatBudgetAmount(consumedBudgetAmount),
    remaining: formatBudgetAmount(remainingBudgetAmount),
    progress,
    progressLabel: `${progress.toFixed(1)}%`,
    color: "#3F7ECC",
  }];
};

const buildBudgetBoostEventCountQuery = (selectedSeasonData, dateRange) => {
  const seasonCondition = buildGameSpaceIdCondition(selectedSeasonData, "be");
  const utcRange = buildUtcRange(dateRange);

  if (!seasonCondition || !utcRange) {
    return null;
  }

  return {
    query: `
      SELECT COUNT(DISTINCT be.id) AS boost_event_count
      FROM boost_event be
      WHERE ${seasonCondition.replace(/^AND\s+/, "")}
        AND be.start_datetime < '${utcRange.endUtc}'
        AND be.end_datetime >= '${utcRange.startUtc}'
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const buildBudgetBoostSourceQuery = (selectedSeasonData, dateRange) => {
  const seasonCondition = buildGameSpaceIdCondition(selectedSeasonData, "be");
  const utcRange = buildUtcRange(dateRange);

  if (!seasonCondition || !utcRange) {
    return null;
  }

  return {
    query: `
      WITH selected_events AS (
        SELECT
          be.id AS boost_event_id,
          be.game_space_id,
          be.start_datetime,
          be.end_datetime,
          cb.id AS capital_budget_id,
          COALESCE(
            (
              SELECT NULLIF(TRIM(bei.event_name), '')
              FROM boost_event_info bei
              WHERE bei.boost_event_id = be.id
                AND bei.language_code = 'JA'
              LIMIT 1
            ),
            (
              SELECT NULLIF(TRIM(bel.label), '')
              FROM boost_event_label bel
              WHERE bel.boost_event_id = be.id
                AND bel.language_code = 'JA'
                AND bel.sort_order = 1
              LIMIT 1
            ),
            CAST(be.id AS CHAR)
          ) AS boost_event_name
        FROM boost_event be
        JOIN capital_budget cb
          ON cb.boost_event_id = be.id
        WHERE ${seasonCondition.replace(/^AND\s+/, "")}
          AND be.start_datetime < '${utcRange.endUtc}'
          AND be.end_datetime >= '${utcRange.startUtc}'
      ),
      source_totals AS (
        SELECT
          se.boost_event_id,
          se.boost_event_name,
          SUM(b.initial_balance_amount) AS total_budget_amount,
          COALESCE(SUM(pabeorg.reward_amount), 0) AS consumed_budget_amount
        FROM selected_events se
        JOIN budget b
          ON b.capital_budget_id = se.capital_budget_id
        LEFT JOIN budget_event pabe
          ON pabe.budget_id = b.id
         AND pabe.occurred_datetime >= '${utcRange.startUtc}'
         AND pabe.occurred_datetime < '${utcRange.endUtc}'
        LEFT JOIN budget_event_of_reward_granted pabeorg
          ON pabeorg.id = pabe.id
        GROUP BY
          se.boost_event_id,
          se.boost_event_name
      )
      SELECT
        st.boost_event_id,
        st.boost_event_name,
        st.total_budget_amount,
        st.consumed_budget_amount,
        (st.total_budget_amount - st.consumed_budget_amount) AS remaining_budget_amount
      FROM source_totals st
      ORDER BY st.boost_event_id
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const buildBudgetBoostShotAggregationQuery = (selectedSeasonData, dateRange) => {
  const seasonCondition = buildGameSpaceIdCondition(selectedSeasonData, "be");
  const utcRange = buildUtcRange(dateRange);

  if (!seasonCondition || !utcRange) {
    return null;
  }

  return {
    query: `
      WITH selected_events AS (
        SELECT
          be.id AS boost_event_id,
          be.game_space_id,
          be.start_datetime,
          be.end_datetime,
          cb.id AS capital_budget_id,
          COALESCE(
            (
              SELECT NULLIF(TRIM(bei.event_name), '')
              FROM boost_event_info bei
              WHERE bei.boost_event_id = be.id
                AND bei.language_code = 'JA'
              LIMIT 1
            ),
            (
              SELECT NULLIF(TRIM(bel.label), '')
              FROM boost_event_label bel
              WHERE bel.boost_event_id = be.id
                AND bel.language_code = 'JA'
                AND bel.sort_order = 1
              LIMIT 1
            ),
            CAST(be.id AS CHAR)
          ) AS boost_event_name
        FROM boost_event be
        JOIN capital_budget cb
          ON cb.boost_event_id = be.id
        WHERE ${seasonCondition.replace(/^AND\s+/, "")}
          AND be.start_datetime < '${utcRange.endUtc}'
          AND be.end_datetime >= '${utcRange.startUtc}'
      ),
      target_assets AS (
        SELECT
          se.boost_event_id,
          COUNT(DISTINCT btal.power_asset_id) AS target_count
        FROM selected_events se
        JOIN boost_target_asset_list btal
          ON btal.boost_event_id = se.boost_event_id
        JOIN budget b
          ON b.capital_budget_id = se.capital_budget_id
         AND b.power_asset_id = btal.power_asset_id
        LEFT JOIN budget_event pre_budget_event
          ON pre_budget_event.budget_id = b.id
         AND pre_budget_event.occurred_datetime < se.start_datetime
        LEFT JOIN budget_event_of_reward_granted pre_reward
          ON pre_reward.id = pre_budget_event.id
        WHERE NOT EXISTS (
          SELECT 1
          FROM power_asset_event ppae
          WHERE ppae.game_space_id = se.game_space_id
            AND ppae.power_asset_id = btal.power_asset_id
            AND ppae.type = 'PHOTOGRAPH'
            AND ppae.occurred_datetime < se.start_datetime
        )
        AND pre_reward.id IS NULL
        GROUP BY se.boost_event_id
      ),
      shot_rows AS (
        SELECT
          se.boost_event_id,
          COUNT(DISTINCT pabeorg.review_request_id) AS shot_count,
          COALESCE(SUM(pabeorg.reward_amount), 0) AS payout_amount
        FROM selected_events se
        JOIN budget b
          ON b.capital_budget_id = se.capital_budget_id
        JOIN budget_event pabe
          ON pabe.budget_id = b.id
        JOIN budget_event_of_reward_granted pabeorg
          ON pabeorg.id = pabe.id
        JOIN review_request rr
          ON rr.id = pabeorg.review_request_id
        JOIN boost_target_asset_list btal
          ON btal.boost_event_id = se.boost_event_id
         AND btal.power_asset_id = rr.power_asset_id
        WHERE pabe.occurred_datetime >= se.start_datetime
          AND pabe.occurred_datetime < se.end_datetime
        GROUP BY se.boost_event_id
      )
      SELECT
        se.boost_event_id,
        se.boost_event_name,
        COALESCE(ta.target_count, 0) AS target_count,
        COALESCE(sr.shot_count, 0) AS shot_count,
        COALESCE(sr.payout_amount, 0) AS payout_amount
      FROM selected_events se
      LEFT JOIN target_assets ta
        ON ta.boost_event_id = se.boost_event_id
      LEFT JOIN shot_rows sr
        ON sr.boost_event_id = se.boost_event_id
      ORDER BY se.start_datetime, se.boost_event_id
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const buildBudgetRemainTrendQuery = (companyCode, selectedSeasonData, dateRange) => {
  const seasonCondition = buildGameSpaceCondition(selectedSeasonData);
  const utcRange = buildUtcRange(dateRange);
  const endUtc = utcRange?.endUtc;
  const startUtc = utcRange?.startUtc;
  const capitalBudgetIds = getBudgetTrendIds(companyCode);
  const capitalBudgetCondition = capitalBudgetIds.length > 0
    ? `AND pab.capital_budget_id IN (${capitalBudgetIds.map((id) => `'${id}'`).join(", ")})`
    : "";

  if (!seasonCondition || !startUtc || !endUtc) {
    return null;
  }

  return {
    query: `
      WITH RECURSIVE date_series AS (
        SELECT DATE(CONVERT_TZ('${startUtc}', 'UTC', 'Asia/Tokyo')) AS target_date
        UNION ALL
        SELECT DATE_ADD(target_date, INTERVAL 1 DAY)
        FROM date_series
        WHERE target_date < DATE(CONVERT_TZ('${endUtc}', 'UTC', 'Asia/Tokyo'))
      ),
      target_budget AS (
        SELECT
          pab.id AS budget_id,
          pab.capital_budget_id,
          pab.initial_balance_amount
        ${buildBudgetBaseJoinClause(companyCode)}
        WHERE ${seasonCondition.replace(/^AND\s+/, "")}
          ${capitalBudgetCondition}
        GROUP BY
          pab.id,
          pab.capital_budget_id,
          pab.initial_balance_amount
      ),
      budget_total AS (
        SELECT
          capital_budget_id,
          SUM(initial_balance_amount) AS total_initial_balance_amount
        FROM target_budget
        GROUP BY capital_budget_id
      ),
      daily_reward AS (
        SELECT
          tb.capital_budget_id,
          DATE(CONVERT_TZ(pabe.occurred_datetime, 'UTC', 'Asia/Tokyo')) AS target_date,
          SUM(pabeorg.reward_amount) AS daily_reward_amount
        FROM target_budget tb
        JOIN budget_event pabe
          ON pabe.budget_id = tb.budget_id
         AND pabe.occurred_datetime < '${endUtc}'
        JOIN budget_event_of_reward_granted pabeorg
          ON pabeorg.id = pabe.id
        GROUP BY
          tb.capital_budget_id,
          DATE(CONVERT_TZ(pabe.occurred_datetime, 'UTC', 'Asia/Tokyo'))
      ),
      budget_keys AS (
        SELECT capital_budget_id
        FROM budget_total
      ),
      date_budget AS (
        SELECT
          bk.capital_budget_id,
          ds.target_date
        FROM budget_keys bk
        CROSS JOIN date_series ds
      ),
      daily_running AS (
        SELECT
          db.capital_budget_id,
          db.target_date,
          COALESCE(dr.daily_reward_amount, 0) AS daily_reward_amount,
          SUM(COALESCE(dr.daily_reward_amount, 0)) OVER (
            PARTITION BY db.capital_budget_id
            ORDER BY db.target_date
            ROWS UNBOUNDED PRECEDING
          ) AS cumulative_reward_amount
        FROM date_budget db
        LEFT JOIN daily_reward dr
          ON dr.capital_budget_id = db.capital_budget_id
         AND dr.target_date = db.target_date
      )
      SELECT
        dr.capital_budget_id,
        dr.target_date,
        bt.total_initial_balance_amount - dr.cumulative_reward_amount AS remaining_amount
      FROM daily_running dr
      JOIN budget_total bt
        ON bt.capital_budget_id = dr.capital_budget_id
      ORDER BY dr.capital_budget_id, dr.target_date
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const shouldUseBudgetRemainTrendRds = (companyCode, selectedSeasonData) => {
  if (!BUDGET_REMAIN_TREND_RDS_COMPANY_CODES[companyCode]) {
    return false;
  }

  const seasons = Array.isArray(selectedSeasonData) ? selectedSeasonData : [];
  if (seasons.length !== 1) {
    return false;
  }

  const season = seasons[0];
  return String(season?.seasonId || "") === BUDGET_REMAIN_TREND_RDS_SEASON_ID && isRegularSeason(season);
};

const buildBudgetRemainTrendRdsQuery = (companyCode, dateRange) => {
  const rangeDates = getRangeDates(dateRange);
  const startDate = rangeDates[0];
  const endDate = rangeDates[rangeDates.length - 1];
  const rdsCompanyCode = BUDGET_REMAIN_TREND_RDS_COMPANY_CODES[companyCode];

  if (!startDate || !endDate || !rdsCompanyCode) {
    return null;
  }

  const capitalBudgetIds = getBudgetTrendIds(companyCode);
  if (capitalBudgetIds.length === 0) {
    return null;
  }

  return {
    "query": `
      SELECT
        companyCode AS company_code,
        capitalBudgetId AS capital_budget_id,
        targetDateJst AS target_date,
        remainingAmount AS remaining_amount
      FROM ${BUDGET_REMAIN_TREND_RDS_TABLE}
      WHERE companyCode = '${rdsCompanyCode}'
        AND capitalBudgetId IN (${capitalBudgetIds.map((id) => `'${id}'`).join(", ")})
        AND targetDateJst >= '${startDate}'
        AND targetDateJst <= '${endDate}'
      ORDER BY capitalBudgetId, targetDateJst
    `,
    "database": "KpiDashboard",
    "type": "list",
  };
};

const fetchRows = function* (query) {
  const jwtToken = yield call(getJwtToken);
  return yield call(getDataFromSxi, query, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken);
};

const fetchRdsRows = function* (query) {
  const jwtToken = yield call(getJwtToken);
  return yield call(getDataFromRDS, query, ENDPOINT.GET_DATA_RDS, jwtToken);
};

const normalizeRdsBudgetTrendRows = (rows) => {
  const parsedRows = JSON.parse(rows?.body || "[]");

  return parsedRows.map((row) => ({
    company_code: row?.[0]?.stringValue || null,
    capital_budget_id: row?.[1]?.stringValue || null,
    target_date: row?.[2]?.stringValue || null,
    remaining_amount: row?.[3]?.longValue ?? row?.[3]?.doubleValue ?? row?.[3]?.stringValue ?? null,
  }));
};

const normalizeBudgetTrend = (rows, companyCode, selectedDateRange) => {
  const dateList = getRangeDates(selectedDateRange);
  const groupedByBudget = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const capitalBudgetId = row?.capital_budget_id || null;
    if (!capitalBudgetId) return;

    if (!groupedByBudget.has(capitalBudgetId)) {
      groupedByBudget.set(capitalBudgetId, {
        capitalBudgetId,
        days: new Map(),
      });
    }
    const group = groupedByBudget.get(capitalBudgetId);

    if (row?.target_date) {
      const targetDate = moment.utc(row.target_date).tz("Asia/Tokyo").format("YYYY-MM-DD");
      group.days.set(targetDate, toYenAmount(row?.remaining_amount || 0));
    }
  });

  const orderedBudgetIds = [
    ...getBudgetTrendIds(companyCode),
    ...Array.from(groupedByBudget.keys()).filter((id) => !getBudgetTrendIds(companyCode).includes(id)),
  ].filter((id, index, self) => self.indexOf(id) === index);

  const trend = { date: dateList.map((date) => moment(date).format("M/D")) };
  const chartConfig = {};
  const legend = [];
  const colorValue = [];
  const bySourceRows = [];

  orderedBudgetIds.forEach((capitalBudgetId, index) => {
    const group = groupedByBudget.get(capitalBudgetId);
    if (!group) return;

    const meta = getBudgetTrendMeta(companyCode, capitalBudgetId, index);
    const seriesKey = `count_${String(capitalBudgetId).replace(/[^a-zA-Z0-9]/g, "_")}`;
    const values = dateList.map((dateKey) => {
      const remainingAmount = group.days.get(dateKey);
      return remainingAmount === undefined ? null : toMillionValue(remainingAmount);
    });

    trend[seriesKey] = values;
    chartConfig[seriesKey] = meta.name;
    legend.push(meta.name);
    colorValue.push(meta.color);

    const lastRemainingAmount = [...group.days.entries()].length > 0
      ? [...group.days.entries()]
        .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
        .reduce((_, [, remainingAmount]) => remainingAmount, null)
      : null;

    bySourceRows.push({
      code: meta.code,
      name: meta.name,
      budget: "¥--",
      used: "¥--",
      remaining: lastRemainingAmount === null ? "¥--" : `¥${toMillionValue(lastRemainingAmount)}M`,
      progress: 0,
      color: meta.color,
    });
  });

  return {
    trend,
    legend,
    chartConfig,
    colorValue,
    bySourceRows,
  };
};

const buildEmptyTrendPayload = () => ({
  trend: { date: [] },
  legend: [],
  chartConfig: {},
  colorValue: [],
  bySourceRows: [],
});

const normalizeBudgetBoostRows = (rows) => (
  Array.isArray(rows) ? rows : []
).map((row) => ({
  id: row?.boost_event_id || null,
  name: row?.boost_event_name || row?.boost_event_id || "ブースト",
  target: Number(row?.target_count || 0).toLocaleString("ja-JP"),
  shots: Number(row?.shot_count || 0).toLocaleString("ja-JP"),
  payout: formatBudgetAmount(toYenAmount(row?.payout_amount || 0)),
}));

const normalizeBudgetBoostSourceRows = (rows) => (
  Array.isArray(rows) ? rows : []
).map((row, index) => {
  const totalBudgetAmount = toYenAmount(row?.total_budget_amount || 0);
  const consumedBudgetAmount = toYenAmount(row?.consumed_budget_amount || 0);
  const remainingBudgetAmount = toYenAmount(row?.remaining_budget_amount || 0);
  const progress = totalBudgetAmount > 0
    ? Number(((consumedBudgetAmount / totalBudgetAmount) * 100).toFixed(1))
    : 0;

  return {
    code: row?.boost_event_id || `BOOST-${index + 1}`,
    name: row?.boost_event_name || row?.boost_event_id || "ブースト",
    budgetAmount: totalBudgetAmount,
    usedAmount: consumedBudgetAmount,
    remainingAmount: remainingBudgetAmount,
    budget: formatBudgetAmount(totalBudgetAmount),
    used: formatBudgetAmount(consumedBudgetAmount),
    remaining: formatBudgetAmount(remainingBudgetAmount),
    progress,
    progressLabel: `${progress.toFixed(1)}%`,
    color: FALLBACK_COLORS[index % FALLBACK_COLORS.length],
  };
});

function* handleBudgetBoostShotAggregation(action) {
  const companyCode = String(action?.payload?.companyCode || "tepco");
  const { selectedSeason, dateRange } = getBudgetSelectors(companyCode);
  const setBoost = getBudgetBoostSetter(companyCode);
  const setBoostLoading = getBudgetBoostLoadingSetter(companyCode);

  try {
    yield put(setBoostLoading(true));
    const selectedSeasonData = yield select(selectedSeason);
    const selectedDateRange = yield select(dateRange);

    if (!Array.isArray(selectedSeasonData) || selectedSeasonData.length === 0) {
      yield put(setBoost([]));
      return;
    }

    const boostQuery = buildBudgetBoostShotAggregationQuery(selectedSeasonData, selectedDateRange);

    if (!boostQuery) {
      yield put(setBoost([]));
      return;
    }

    const boostRows = yield* fetchRows(boostQuery);
    yield put(setBoost(normalizeBudgetBoostRows(boostRows)));
  } catch (error) {
    console.log(error);
    yield put(setBoost([]));
  } finally {
    yield put(setBoostLoading(false));
  }
}

function* handleBudgetBoostSourceData(action) {
  const companyCode = String(action?.payload?.companyCode || "tepco");
  const { selectedSeason, dateRange } = getBudgetSelectors(companyCode);
  const setBoostSource = getBudgetBoostSourceSetter(companyCode);
  const setBoostSourceLoading = getBudgetBoostSourceLoadingSetter(companyCode);

  try {
    yield put(setBoostSourceLoading(true));
    const selectedSeasonData = yield select(selectedSeason);
    const selectedDateRange = yield select(dateRange);

    if (!Array.isArray(selectedSeasonData) || selectedSeasonData.length === 0) {
      yield put(setBoostSource([]));
      return;
    }

    const boostCountQuery = buildBudgetBoostEventCountQuery(selectedSeasonData, selectedDateRange);
    const boostSourceQuery = buildBudgetBoostSourceQuery(selectedSeasonData, selectedDateRange);

    if (!boostCountQuery || !boostSourceQuery) {
      yield put(setBoostSource([]));
      return;
    }

    const [boostCountRows, boostSourceRows] = [
      yield* fetchRows(boostCountQuery),
      yield* fetchRows(boostSourceQuery),
    ];

    const boostEventCount = extractSingleRowValue(boostCountRows, "boost_event_count");
    yield put(setBoostSource({
      rows: normalizeBudgetBoostSourceRows(boostSourceRows),
      boostEventCount,
    }));
  } catch (error) {
    console.log(error);
    yield put(setBoostSource([]));
  } finally {
    yield put(setBoostSourceLoading(false));
  }
}

// eslint-disable-next-line no-unused-vars
function* handleBudgetSummaryData(action) {
  const companyCode = String(action?.payload?.companyCode || "tepco");
  const { selectedSeason, dateRange } = getBudgetSelectors(companyCode);
  const setSummary = getBudgetSummarySetter(companyCode);
  const setBoost = getBudgetBoostSetter(companyCode);
  const setBoostLoading = getBudgetBoostLoadingSetter(companyCode);
  const setBoostSource = getBudgetBoostSourceSetter(companyCode);
  const setSourceLoading = getBudgetSourceLoadingSetter(companyCode);

  yield put(setPageTopLoader(true));
  yield put(setBoostLoading(true));
  yield put(setSourceLoading(true));
  yield put(setBoost([]));
  yield put(setBoostSource([]));

  try {
    const selectedSeasonData = yield select(selectedSeason);
    const selectedDateRange = yield select(dateRange);

    if (!Array.isArray(selectedSeasonData) || selectedSeasonData.length === 0) {
      yield put(setSummary(null));
      yield put(setBoostSource([]));
      yield put(setBoost([]));
      return;
    }

    const totalQuery = buildBudgetSummaryTotalQuery(companyCode, selectedSeasonData);
    const consumedQuery = buildBudgetSummaryConsumedQuery(companyCode, selectedSeasonData, selectedDateRange);
    const remainingQuery = buildBudgetSummaryRemainingQuery(companyCode, selectedSeasonData);

    if (!totalQuery || !consumedQuery || !remainingQuery) {
      yield put(setSummary(null));
      yield put(setBoostSource([]));
      yield put(setBoost([]));
      return;
    }

    const [totalRows, consumedRows, remainingRows] = [
      yield* fetchRows(totalQuery),
      yield* fetchRows(consumedQuery),
      yield* fetchRows(remainingQuery),
    ];

    const totalBudgetAmount = toYenAmount(extractSingleRowValue(totalRows, "total_budget_amount"));
    const consumedBudgetAmount = toYenAmount(extractSingleRowValue(consumedRows, "consumed_budget_amount"));
    const remainingBudgetAmount = toYenAmount(extractSingleRowValue(remainingRows, "remaining_budget_amount"));
    const consumedRate =
      totalBudgetAmount > 0
        ? Number(((consumedBudgetAmount / totalBudgetAmount) * 100).toFixed(1))
        : null;

    yield put(
      setSummary({
        companyCode,
        totalBudgetAmount,
        consumedBudgetAmount,
        consumedRate,
        remainingBudgetAmount,
        bySourceRows: buildBudgetSummarySourceRows(
          companyCode,
          totalBudgetAmount,
          consumedBudgetAmount,
          remainingBudgetAmount
        ),
      })
    );
  } finally {
    yield put(setPageTopLoader(false));
    yield put(setSourceLoading(false));
  }
}

function* handleBudgetRemainTrend(action) {
  const companyCode = String(action?.payload?.companyCode || "tepco");
  const { selectedSeason, dateRange } = getBudgetSelectors(companyCode);
  const setTrend = getBudgetTrendSetter(companyCode);

  try {
    const selectedSeasonData = yield select(selectedSeason);
    const selectedDateRange = yield select(dateRange);

    if (!Array.isArray(selectedSeasonData) || selectedSeasonData.length === 0) {
      yield put(setTrend(buildEmptyTrendPayload()));
      return;
    }

    const useRdsTrend = shouldUseBudgetRemainTrendRds(companyCode, selectedSeasonData);
    let trendRows = [];

    if (useRdsTrend) {
      const trendQuery = buildBudgetRemainTrendRdsQuery(companyCode, selectedDateRange);
      if (!trendQuery) {
        yield put(setTrend(buildEmptyTrendPayload()));
        return;
      }
      const rdsRows = yield* fetchRdsRows(trendQuery);
      trendRows = normalizeRdsBudgetTrendRows(rdsRows);
    } else {
      const trendQuery = buildBudgetRemainTrendQuery(companyCode, selectedSeasonData, selectedDateRange);
      if (!trendQuery) {
        yield put(setTrend(buildEmptyTrendPayload()));
        return;
      }
      trendRows = yield* fetchRows(trendQuery);
    }

    const normalizedTrend = normalizeBudgetTrend(trendRows, companyCode, selectedDateRange);

    yield put(setTrend(normalizedTrend));
  } catch (error) {
    console.log(error);
    yield put(setTrend(buildEmptyTrendPayload()));
  }
}

export default function* pictreeBudgetSaga() {
  yield takeEvery(PICTREE.GET_BUDGET_SUMMARY_DATA, handleBudgetSummaryData);
  yield takeEvery(PICTREE.GET_BUDGET_REMAIN_TREND, handleBudgetRemainTrend);
  yield takeEvery(PICTREE.GET_BUDGET_BOOST_DATA, handleBudgetBoostShotAggregation);
  yield takeEvery(PICTREE.GET_BUDGET_BOOST_SOURCE_DATA, handleBudgetBoostSourceData);
}
